import type { FrameTargetAnnotationDraft, SavedFrameTargetAnnotation } from "./types";

export type AnnotationTargetContent = Pick<
  FrameTargetAnnotationDraft | SavedFrameTargetAnnotation,
  "points" | "contours" | "maskSource"
>;

function samePoint(
  left: AnnotationTargetContent["points"][number],
  right: AnnotationTargetContent["points"][number],
): boolean {
  return (
    left.id === right.id && left.kind === right.kind && left.x === right.x && left.y === right.y
  );
}

function sameContours(
  left: AnnotationTargetContent["contours"],
  right: AnnotationTargetContent["contours"],
): boolean {
  if (left === undefined || right === undefined) return left === right;
  return (
    left.length === right.length &&
    left.every(
      (contour, contourIndex) =>
        contour.length === right[contourIndex]?.length &&
        contour.every((point, pointIndex) => {
          const candidate = right[contourIndex]?.[pointIndex];
          return candidate !== undefined && point.x === candidate.x && point.y === candidate.y;
        }),
    )
  );
}

function sameMaskSource(
  left: AnnotationTargetContent["maskSource"],
  right: AnnotationTargetContent["maskSource"],
): boolean {
  if (left === undefined || right === undefined) return left === right;
  return left.id === right.id && left.revision === right.revision;
}

export function hasAnnotationOverlay(target: AnnotationTargetContent): boolean {
  return target.contours !== undefined || target.maskSource !== undefined;
}

export function hasAnnotationTargetContent(target: AnnotationTargetContent): boolean {
  return target.points.length > 0 || hasAnnotationOverlay(target);
}

/** pointRevision은 비동기 stale 판정용이므로 저장 baseline과의 내용 비교에서는 제외한다. */
export function hasSameAnnotationTargetContent(
  left: AnnotationTargetContent | undefined,
  right: AnnotationTargetContent | undefined,
): boolean {
  const leftEmpty = left === undefined || !hasAnnotationTargetContent(left);
  const rightEmpty = right === undefined || !hasAnnotationTargetContent(right);
  if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty;

  return (
    left.points.length === right.points.length &&
    left.points.every((point, index) => {
      const candidate = right.points[index];
      return candidate !== undefined && samePoint(point, candidate);
    }) &&
    sameContours(left.contours, right.contours) &&
    sameMaskSource(left.maskSource, right.maskSource)
  );
}
